/**
 * Compares one document's extracted rows against the workstream's current figures, for the review overlay.
 *
 * Standalone (not in the `src/utils` barrel) so it can carry its own vitest — the barrel contains JSX.
 *
 * Three states per extracted row:
 *  - `new`: no current figure for this datapoint — accepting simply adds it
 *  - `matches`: the current figure already holds this value
 *  - `differs`: the current figure holds a different value — accepting replaces it
 *
 * WHY a current figure differs (hand-edited, another document, a later re-read) is deliberately not decided
 * here: a curated row keeps its original source file name after a hand edit, so the file name alone cannot
 * say where the current number came from. The (i) overlay shows the current figure's own history instead.
 *
 * A current figure is the same datapoint when its name (normalised exactly as `buildTargetView` and Java's
 * `FoundationNames.normalise` do) and scope match. Unit is NOT part of the match: the same datapoint in a
 * different unit is a difference a reviewer needs to see, not a different fact to hide.
 */

export const REVIEW_STATE = Object.freeze({ NEW: "new", MATCHES: "matches", DIFFERS: "differs" });

const normalise = (value) => (value == null ? "" : String(value).trim().replace(/\s+/g, " ").toLowerCase());

const keyOf = (row) => `${normalise(row.dataKey)}|${normalise(row.scope)}`;

/** Two values are the same figure when their parsed numbers agree, or (no number) their text does. */
const sameValue = (a, b) => {
  if (normalise(a.unit) !== normalise(b.unit)) return false;
  if (a.valueNumeric != null && b.valueNumeric != null) {
    return Number(a.valueNumeric) === Number(b.valueNumeric);
  }
  return normalise(a.dataValue) === normalise(b.dataValue);
};

/**
 * @param {Array} extractedRows this document's rows
 * @param {Array} currentRows the workstream's current (working-set) figures
 * @returns {Map<string|number, {state: string, current: (object|null)}>} keyed on the extracted row's id,
 *   one entry per extracted row
 */
export function compareWithCurrent(extractedRows, currentRows) {
  const currentByKey = new Map();
  (currentRows || []).forEach((row) => {
    if (row) currentByKey.set(keyOf(row), row);
  });
  const result = new Map();
  (extractedRows || []).forEach((row) => {
    if (!row) return;
    const current = currentByKey.get(keyOf(row)) ?? null;
    let state = REVIEW_STATE.NEW;
    if (current) state = sameValue(row, current) ? REVIEW_STATE.MATCHES : REVIEW_STATE.DIFFERS;
    result.set(row.id, { state, current });
  });
  return result;
}

/**
 * How a current figure came to be what it is, and whether it is approved — from fields every curated row
 * carries (`origin`, `edited`, `published`, `version`), so no extra request.
 *
 * `origin` USER means the datapoint was typed in by hand; `edited` means an extracted figure was corrected by
 * hand afterwards, in which case the source file name it still carries is where it was FIRST read from, not
 * where the current number came from — the phrasing has to say so.
 *
 * @returns {{how: string, status: ("Approved"|"Pending approval"|null), version: (number|null)}}
 */
export function figureHistory(row) {
  if (!row) return { how: "", status: null, version: null };
  const file = row.sourceFileName;
  let how;
  if (row.origin === "USER") how = "Entered by hand";
  else if (row.edited) how = file ? `Extracted from ${file}, then edited by hand` : "Edited by hand";
  else how = file ? `Extracted from ${file}` : "Extracted from a source document";
  let status = null;
  if (row.published === true) status = "Approved";
  else if (row.published === false) status = "Pending approval";
  return { how, status, version: row.version ?? null };
}
